import type { Graphics } from 'pixi.js';
import type { Island } from './world.js';
import { isInsideIsland } from './world.js';

function makeRng(seed: number): () => number {
  let s = seed;
  return function () {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type AnimalType = 'cow' | 'pig' | 'hen' | 'chicken';

export interface Animal {
  type: AnimalType;
  x: number;
  y: number;
  angle: number;
  wanderTimer: number;
  islandIdx: number;
}

export function generateAnimals(islands: Island[]): Animal[] {
  const rng = makeRng(0xa71a1234);
  const animals: Animal[] = [];

  for (let i = 0; i < islands.length; i++) {
    const isl = islands[i];
    const groups: [AnimalType, number][] = [
      ['cow',     rng() < 0.3 ? 1 : 0],
      ['pig',     rng() < 0.5 ? 1 : 0],
      ['hen',     Math.floor(rng() * 3)],       // 0-2
      ['chicken', Math.floor(rng() * 4)],       // 0-3
    ];
    for (const [type, count] of groups) {
      for (let j = 0; j < count; j++) {
        for (let attempt = 0; attempt < 30; attempt++) {
          const a = rng() * Math.PI * 2;
          const d = rng() * isl.radius * 0.55;
          const x = isl.cx + Math.cos(a) * d;
          const y = isl.cy + Math.sin(a) * d;
          if (isInsideIsland({ x, y }, isl)) {
            animals.push({ type, x, y, angle: rng() * Math.PI * 2, wanderTimer: rng() * 120, islandIdx: i });
            break;
          }
        }
      }
    }
  }
  return animals;
}

const SPEEDS: Record<AnimalType, number> = { cow: 0.14, pig: 0.18, hen: 0.16, chicken: 0.22 };

export function updateAnimals(
  animals: Animal[],
  islands: Island[],
  dt: number,
  killedAnimals: Set<number>,
  heldAnimalIdx: number | null,
): void {
  for (let i = 0; i < animals.length; i++) {
    if (killedAnimals.has(i)) continue;
    if (i === heldAnimalIdx) continue; // controlled by player
    const a = animals[i];
    a.wanderTimer -= dt;
    if (a.wanderTimer <= 0) {
      a.angle = Math.random() * Math.PI * 2;
      a.wanderTimer = 80 + Math.random() * 200;
    }
    const spd = SPEEDS[a.type];
    const nx = a.x + Math.cos(a.angle) * spd * dt;
    const ny = a.y + Math.sin(a.angle) * spd * dt;
    if (isInsideIsland({ x: nx, y: ny }, islands[a.islandIdx])) {
      a.x = nx;
      a.y = ny;
    } else {
      a.angle += Math.PI + (Math.random() - 0.5) * 1.2;
      a.wanderTimer = 40 + Math.random() * 80;
    }
  }
}

/** Visible world-space rectangle, used to skip off-screen geometry. */
export interface ViewBounds { x0: number; y0: number; x1: number; y1: number }

/**
 * Draws every living animal. Each animal costs ~7 fill/stroke calls, and since
 * the Graphics is cleared and rebuilt every frame that geometry is
 * re-tessellated from scratch — so with ~190 animals spread over a 8000x8000
 * world, drawing the ones nowhere near the camera dominated the frame budget.
 *
 * Pass `view` to skip animals outside it. Animals are single-instance (the 3x3
 * world tiling only replicates islands), so anything outside the viewport
 * contributes nothing to the framebuffer and culling it is visually identical.
 * Omitting `view` draws everything, as before.
 */
export function drawAnimals(
  g: Graphics,
  animals: Animal[],
  killedAnimals: Set<number>,
  view?: ViewBounds,
): void {
  for (let i = 0; i < animals.length; i++) {
    if (killedAnimals.has(i)) continue;
    const a = animals[i];
    if (view && (a.x < view.x0 || a.x > view.x1 || a.y < view.y0 || a.y > view.y1)) continue;
    switch (a.type) {
      case 'cow':     drawCow(g, a.x, a.y, a.angle);     break;
      case 'pig':     drawPig(g, a.x, a.y, a.angle);     break;
      case 'hen':     drawHen(g, a.x, a.y, a.angle);     break;
      case 'chicken': drawChicken(g, a.x, a.y, a.angle); break;
    }
  }
}

/** Returns index of nearest living animal within maxDist, or -1. */
export function findAnimalNear(
  animals: Animal[],
  killedAnimals: Set<number>,
  x: number,
  y: number,
  maxDist: number,
): number {
  let best = -1, bestD2 = maxDist * maxDist;
  for (let i = 0; i < animals.length; i++) {
    if (killedAnimals.has(i)) continue;
    const dx = animals[i].x - x, dy = animals[i].y - y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) { bestD2 = d2; best = i; }
  }
  return best;
}

/** Find which island index a position belongs to. */
export function findIslandForPos(pos: { x: number; y: number }, islands: Island[]): number {
  for (let i = 0; i < islands.length; i++) {
    if (isInsideIsland(pos, islands[i])) return i;
  }
  return 0;
}

/** Draw a small animal on a boat at local offset (x, y). */
export function drawAnimalOnBoat(g: Graphics, type: AnimalType, x: number, y: number): void {
  const body    = { cow: 0xd4aa70, pig: 0xf0a8b0, hen: 0xa05028, chicken: 0xf0d848 }[type];
  const outline = { cow: 0x8a5820, pig: 0xd06878, hen: 0x703018, chicken: 0xc8a010 }[type];
  // Body
  g.ellipse(x, y, 5, 4);
  g.fill({ color: body });
  g.ellipse(x, y, 5, 4);
  g.stroke({ color: outline, width: 1 });
  // Head
  g.circle(x + 4.5, y, 2.5);
  g.fill({ color: body });
  g.circle(x + 4.5, y, 2.5);
  g.stroke({ color: outline, width: 0.8 });
  // Tiny comb/horn indicator
  if (type === 'cow') {
    g.moveTo(x + 3, y - 2); g.lineTo(x + 4, y - 4);
    g.moveTo(x + 5, y - 2); g.lineTo(x + 6, y - 4);
    g.stroke({ color: 0x6a3810, width: 1, cap: 'round' });
  } else if (type === 'hen' || type === 'chicken') {
    g.circle(x + 4.5, y - 2.5, 1);
    g.fill({ color: 0xdd2020 });
  }
}

// Cow — slightly larger than person (~20px total)
function drawCow(g: Graphics, x: number, y: number, angle: number): void {
  const px = -Math.sin(angle), py = Math.cos(angle);
  g.ellipse(x + 1, y + 1.5, 10, 7);
  g.fill({ color: 0x000000, alpha: 0.12 });
  g.ellipse(x, y, 10, 7);
  g.fill({ color: 0xd4aa70 });
  g.ellipse(x, y, 10, 7);
  g.stroke({ color: 0x8a5820, width: 1 });
  // Black patch
  g.ellipse(x - 1, y + 1, 4, 3);
  g.fill({ color: 0x333333, alpha: 0.65 });
  // Legs
  for (const [lx, ly] of [
    [x + Math.cos(angle)*5 + px*3,  y + Math.sin(angle)*5 + py*3],
    [x + Math.cos(angle)*5 - px*3,  y + Math.sin(angle)*5 - py*3],
    [x - Math.cos(angle)*5 + px*3,  y - Math.sin(angle)*5 + py*3],
    [x - Math.cos(angle)*5 - px*3,  y - Math.sin(angle)*5 - py*3],
  ] as [number, number][]) {
    g.circle(lx, ly, 1.8);
    g.fill({ color: 0x6a3810 });
  }
  // Head
  const hx = x + Math.cos(angle)*12, hy = y + Math.sin(angle)*12;
  g.circle(hx, hy, 4.5);
  g.fill({ color: 0xc89850 });
  g.circle(hx, hy, 4.5);
  g.stroke({ color: 0x8a5820, width: 1 });
  // Ears
  g.circle(hx + px*3.5, hy + py*3.5, 2);
  g.circle(hx - px*3.5, hy - py*3.5, 2);
  g.fill({ color: 0xe8b080 });
  // Nose
  g.ellipse(hx + Math.cos(angle)*3, hy + Math.sin(angle)*3, 2.5, 1.8);
  g.fill({ color: 0xe8a090 });
  g.circle(hx + Math.cos(angle)*3.5 + px*0.8, hy + Math.sin(angle)*3.5 + py*0.8, 0.6);
  g.circle(hx + Math.cos(angle)*3.5 - px*0.8, hy + Math.sin(angle)*3.5 - py*0.8, 0.6);
  g.fill({ color: 0xa05040 });
}

// Pig — slightly smaller than person (~10px total)
function drawPig(g: Graphics, x: number, y: number, angle: number): void {
  const px = -Math.sin(angle), py = Math.cos(angle);
  g.ellipse(x + 0.8, y + 1, 7, 5.5);
  g.fill({ color: 0x000000, alpha: 0.10 });
  g.ellipse(x, y, 7, 5.5);
  g.fill({ color: 0xf0a8b0 });
  g.ellipse(x, y, 7, 5.5);
  g.stroke({ color: 0xd06878, width: 0.8 });
  // Head
  const hx = x + Math.cos(angle)*8, hy = y + Math.sin(angle)*8;
  g.circle(hx, hy, 3.8);
  g.fill({ color: 0xf0a8b0 });
  g.circle(hx, hy, 3.8);
  g.stroke({ color: 0xd06878, width: 0.8 });
  // Ears
  g.circle(hx + px*2.8, hy + py*2.8, 1.8);
  g.circle(hx - px*2.8, hy - py*2.8, 1.8);
  g.fill({ color: 0xe888a0 });
  // Snout
  g.ellipse(hx + Math.cos(angle)*2.5, hy + Math.sin(angle)*2.5, 2.2, 1.8);
  g.fill({ color: 0xe07888 });
  g.circle(hx + Math.cos(angle)*3 + px*0.7, hy + Math.sin(angle)*3 + py*0.7, 0.55);
  g.circle(hx + Math.cos(angle)*3 - px*0.7, hy + Math.sin(angle)*3 - py*0.7, 0.55);
  g.fill({ color: 0x903050 });
  // Curly tail
  g.circle(x - Math.cos(angle)*7, y - Math.sin(angle)*7, 1.2);
  g.fill({ color: 0xf0a8b0 });
}

// Hen — smaller than pig (~7px total)
function drawHen(g: Graphics, x: number, y: number, angle: number): void {
  const px = -Math.sin(angle), py = Math.cos(angle);
  g.ellipse(x + 0.5, y + 0.8, 5.5, 4);
  g.fill({ color: 0x000000, alpha: 0.10 });
  g.ellipse(x, y, 5.5, 4);
  g.fill({ color: 0xa05028 });
  g.ellipse(x, y, 5.5, 4);
  g.stroke({ color: 0x703018, width: 0.7 });
  // Tail feathers
  const tx = x - Math.cos(angle)*5, ty = y - Math.sin(angle)*5;
  g.moveTo(tx, ty);
  g.lineTo(tx - Math.cos(angle)*3.5 + px*2.5, ty - Math.sin(angle)*3.5 + py*2.5);
  g.moveTo(tx, ty);
  g.lineTo(tx - Math.cos(angle)*3.5 - px*2.5, ty - Math.sin(angle)*3.5 - py*2.5);
  g.moveTo(tx, ty);
  g.lineTo(tx - Math.cos(angle)*4, ty - Math.sin(angle)*4);
  g.stroke({ color: 0x804020, width: 1.5, cap: 'round' });
  // Head
  const hx = x + Math.cos(angle)*6, hy = y + Math.sin(angle)*6;
  g.circle(hx, hy, 2.8);
  g.fill({ color: 0xa05028 });
  // Comb
  g.circle(hx + px*1, hy + py*1, 1.3);
  g.circle(hx, hy - 0.5, 1.1);
  g.fill({ color: 0xdd2020 });
  // Beak
  g.moveTo(hx + Math.cos(angle)*2, hy + Math.sin(angle)*2);
  g.lineTo(hx + Math.cos(angle)*4, hy + Math.sin(angle)*4);
  g.stroke({ color: 0xd4a030, width: 1.5, cap: 'round' });
  // Eye
  g.circle(hx + px*1.2, hy + py*1.2, 0.7);
  g.fill({ color: 0x111111 });
}

// Chicken — smaller than hen (~5px total)
function drawChicken(g: Graphics, x: number, y: number, angle: number): void {
  g.ellipse(x, y, 3.8, 3);
  g.fill({ color: 0xf0d848 });
  g.ellipse(x, y, 3.8, 3);
  g.stroke({ color: 0xc8a010, width: 0.6 });
  const hx = x + Math.cos(angle)*4, hy = y + Math.sin(angle)*4;
  g.circle(hx, hy, 2);
  g.fill({ color: 0xf0d848 });
  // Beak
  g.moveTo(hx + Math.cos(angle)*1.8, hy + Math.sin(angle)*1.8);
  g.lineTo(hx + Math.cos(angle)*3.2, hy + Math.sin(angle)*3.2);
  g.stroke({ color: 0xe09020, width: 1, cap: 'round' });
  // Tiny comb
  g.circle(hx, hy - 1.2, 0.9);
  g.fill({ color: 0xdd3030 });
}
